import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';
import { isEmpty } from 'mojito/utils';
import {
    useRequest,
    useEventsChunkRequest,
    useMarketsChunkRequest,
    useEventsLoadDone,
    useMarketsLoadDone,
} from 'modules/common/hooks/index.js';
import { useCallback, useMemo, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useArrayComparator } from 'presentation/hooks';

const {
    descriptor: PromotionsDescriptor,
    selectors: { selectPromotionsByLocationId, selectLoadingState },
    types: PromotionTypes,
} = MojitoServices.Promotions;
const { createPromotionsDescriptor } = PromotionsDescriptor;
const ServicesUtils = MojitoServices.Common.utils;
const { selectChannel } = MojitoCore.Services.SystemSettings.selectors;
const { selectUserGroups } = MojitoServices.UserInfo.selectors;
const { isLoggedIn } = MojitoServices.Authentication.selectors;

/**
 * Contains custom hooks for promotions functionality.
 *
 * @class PromotionHooks
 * @name hooks
 * @memberof Mojito.Modules.Promotions
 */

/**
 * Fetch promotions from promotions store.
 *
 * @function usePromotions
 *
 * @param {Mojito.Services.Promotions.types.LOCATION} locationId - Promotion's location id.
 * @param {string} page - Applicable page where promotion can be shown.
 *
 * @returns {Mojito.Services.Promotions.types.Promotion[]} Promotions list.
 * @memberof Mojito.Modules.Promotions.hooks
 */
const usePromotions = (locationId, page) => {
    const channels = useSelector(state => {
        let channel = selectChannel(state).toLowerCase();
        channel = isLoggedIn(state) ? `${channel}${PromotionTypes.AUTH_SUFFIX}` : channel;
        const userGroups = selectUserGroups(state) || [];
        const userGroupChannels = userGroups.map(group => `${channel}_${group}`);
        return [channel, ...userGroupChannels];
    }, shallowEqual);
    const descriptors = useMemo(() => [createPromotionsDescriptor(channels)], [channels]);
    useRequest(descriptors);
    return useSelector(
        state => selectPromotionsByLocationId(locationId, page, state),
        shallowEqual
    );
};

/**
 * Hook returns true once promotion locations loading has been finalized
 * (the state of promotion locations is either {@link Mojito.Services.Common.types.CONTENT_STATE|AVAILABLE} or {@link Mojito.Services.Common.types.CONTENT_STATE|UNAVAILABLE})
 * which means that the promotions have been requested, and we got response from a server with or without promotion data.
 *
 * @function usePromotionsLoadDone
 *
 * @returns {boolean} True if all promotions items with provided ids have finalized their loading.
 * @memberof Mojito.Modules.Promotions.hooks
 */
const usePromotionsLoadDone = () => {
    return useSelector(state => {
        const loadingState = selectLoadingState(state);
        return ServicesUtils.isContentLoadDone(loadingState);
    });
};

const getEventIds = promotions => promotions.map(promotion => promotion.event?.id).filter(Boolean);
const getMarketIds = promotions =>
    promotions
        .flatMap(promotion => {
            const { market, boostedMarketId } = promotion.event || {};
            const marketIds = market?.marketId
                ? [market?.marketId, boostedMarketId].filter(Boolean)
                : undefined;
            return (
                marketIds || Object.values(promotion.event?.marketLines || []).map(line => line.id)
            );
        })
        .filter(Boolean);

/**
 * Preload events and markets which are part of promotions on first render in one chunk.
 *
 * @function usePromotionsPreload
 *
 * @param {Array<Mojito.Services.Promotions.types.Promotion>} promotions - Promotions list.
 * @param {boolean} promotionsLoadDone - Flag indicating that promotions loading is done.
 * It is used to properly evaluate the eventsLoadDone and marketsLoadDone output.
 * @param {string} [instanceId] - Component instance id.
 *
 * @returns {Array} First item in the array is a callback function to be triggered to evaluate items to preload. It accepts the list of visible promotion indexes.
 * This callback can be passed inside {@link Mojito.Presentation.Components.Swiper|Swiper} as onPrepareSlidesToRender callback.
 * The second and third items are flags indicating events and markets loading status.
 * @memberof Mojito.Modules.Promotions.hooks
 */
const usePromotionsPreload = (promotions, promotionsLoadDone, instanceId) => {
    const [preloadEventIds, setPreloadEventIds] = useState([]);
    const [preloadMarketIds, setPreloadMarketIds] = useState([]);

    useEventsChunkRequest(preloadEventIds, instanceId);
    useMarketsChunkRequest(preloadMarketIds, instanceId);
    const didEventsChange = useArrayComparator();
    const didMarketsChange = useArrayComparator();
    const evaluateItemsToPreload = useCallback(
        slideIndexes => {
            if (!slideIndexes) {
                return;
            }
            const promotionToPreload = slideIndexes.map(index => promotions[index]).filter(Boolean);
            const eventIds = getEventIds(promotionToPreload);
            const marketIds = getMarketIds(promotionToPreload);
            const eventsDidChange = didEventsChange(eventIds);
            const marketsDidChange = didMarketsChange(marketIds);
            eventIds.length && eventsDidChange && setPreloadEventIds(eventIds);
            marketIds.length && marketsDidChange && setPreloadMarketIds(marketIds);
        },
        [promotions, didEventsChange, didMarketsChange]
    );

    const allEventIds = getEventIds(promotions);
    const allMarketIds = getMarketIds(promotions);
    const noEventsToPreload = promotionsLoadDone && isEmpty(allEventIds);
    const noMarketsToPreload = promotionsLoadDone && isEmpty(allMarketIds);
    // In case of the prefetch (when the store is filled up with the data even before mojito first render) we need to take into consideration
    // all events and markets available in promotions and check their availability in store. If all are available - we can detect immediately that eventsLoadDone and marketsLoadDone,
    // hence no need to show skeleton at all.
    const eventIds = !isEmpty(preloadEventIds) ? preloadEventIds : allEventIds;
    const marketIds = !isEmpty(preloadMarketIds) ? preloadMarketIds : allMarketIds;
    const eventsLoadDone = useEventsLoadDone(eventIds) || noEventsToPreload;
    const marketsLoadDone = useMarketsLoadDone(marketIds) || noMarketsToPreload;
    return [evaluateItemsToPreload, eventsLoadDone, marketsLoadDone];
};

export { usePromotions, usePromotionsPreload, usePromotionsLoadDone };
